import { checkoutPayFlowController } from '@cart-services/core/checkoutPayFlowController'
import * as paymentService from '@cart-services/core/paymentService'
import { type CreditCardDialogContext } from '@cart-services/hooks/credit-card-dialog-types'
import { type CountryCode, PAYMENT_EXCEPTION_CODES } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiFpPaymentExecution } from '@hooks-api/fortune/useApiFpPaymentExecution'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { ECommerceOrderType } from '@hooks-api/types/dataModel/eCommercePayments'
import { type BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { CheckoutPayCreditCardModule } from '@payments/lib/checkout-pay/CheckoutPayCreditCardModule'
import { useEffect, useState } from 'react'
import { useContextPayment } from '../components/PaymentProvider/PaymentProvider'

export interface UseFpB2bCreditCardDialogFlowParams {
  paymentModule: BaseCheckoutPayFlowModule
  countryCode: CountryCode
}

/**
 * B2B CKO Flow 信用卡 dialog flow hook，對齊 useFpB2bApplePayDialogFlow shape。訂單於進 dialog 前
 * 已由 caller 建好，init 以真交易單 id（TRANSACTION）取得 session。分期不走 Flow dialog，installment 恆 null。
 */
export const useFpB2bCreditCardDialogFlow = ({
  paymentModule,
  countryCode,
}: UseFpB2bCreditCardDialogFlowParams) => {
  const { trigger: getPayment } = useApiGetFpPayment()
  const { trigger: executePayment } = useApiFpPaymentExecution()
  const { setIsExecutingPayment } = useContextPayment()
  const [isCreditCardValid, setIsCreditCardValid] = useState<boolean>(false)

  useEffect(function bindCreditCardValidationChange() {
    if (!(paymentModule instanceof CheckoutPayCreditCardModule)) return
    return paymentModule.onValidationChange(setIsCreditCardValid)
  }, [paymentModule])

  const mountCreditCardFields = useCallbackEvent(async (context: CreditCardDialogContext, containerId: string, storedCardContainerId: string) => {
    const initResult = await getPayment({
      order_type: ECommerceOrderType.TRANSACTION,
      order_id: Number(context.paymentTransactionId),
      payment_method_id: context.paymentOptionId,
      remember_payment_card_token: context.rememberPaymentCardToken ?? false,
      installment: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
      email: context.email,
    })
    const paymentInfo = initResult?.data?.data
    if (!paymentInfo) {
      throw new Error('[useFpB2bCreditCardDialogFlow] getPayment returned empty paymentInfo')
    }
    // CkoMount 交還 useCreditCardDialog 持有，dialog 收掉時由它 unmount
    return paymentService.applyCkoSessionForCreditCardDialog(paymentModule, paymentInfo, paymentModule.getAppEnv(), {
      containerId,
      storedCardContainerId,
      countryCode,
    })
  })

  const submitAndExecute = useCallbackEvent(async (context: CreditCardDialogContext) => {
    const ckoPaymentId = await paymentService.submitCreditCardDialogPayment(paymentModule, {
      paymentTransactionId: context.paymentTransactionId,
      orderHash: context.orderHash,
    })
    datadogRum.addAction('creditCardDialog.paymentCompleted', {
      paymentTransactionId: context.paymentTransactionId,
      ckoPaymentId,
    })
    setIsExecutingPayment(true)
    try {
      const { data: paymentResult } = await paymentService.executeWithCkoReconciliationRetry(() => executePayment({
        payment_transaction_id: Number(context.paymentTransactionId),
        service_pay_info: { bank_code: null, apple_pay_info: null, pay_token: null },
        user_payment_token_info: null,
        payment_success_url: context.paymentSuccessUrl,
        payment_fallback_url: context.paymentFallbackUrl,
        payment_option_id: context.paymentOptionId,
      }), {
        extraRetryCodes: [PAYMENT_EXCEPTION_CODES.PAYMENT_API_FAILED],
      })
      if (!paymentResult.redirect_url) {
        throw new Error('[useFpB2bCreditCardDialogFlow] executePayment returned no redirect_url')
      }
      // 用 window.location.href 整頁跳轉以清除 React 狀態
      window.location.href = paymentResult.redirect_url
    }
    catch (error) {
      setIsExecutingPayment(false)
      throw error
    }
  })

  const cancelInFlight = useCallbackEvent((reason: string) => {
    checkoutPayFlowController.cancelInFlight(reason)
  })

  /**
   * 卡片無效時觸發 SDK 顯示各欄位錯誤：fire-and-forget，不經 controller 的 await，
   * 因為 SDK 對無效卡不回 onPaymentCompleted / onError，走 submitAndExecute 會永久 pending 卡死彈窗。
   */
  const revealCardValidationErrors = useCallbackEvent(() => {
    void paymentModule.submit().catch((error) => {
      datadogRum.addError(error, { function: '[useFpB2bCreditCardDialogFlow] revealCardValidationErrors' })
    })
  })

  return {
    mountCreditCardFields,
    submitAndExecute,
    cancelInFlight,
    revealCardValidationErrors,
    isCreditCardValid,
  }
}
